import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import CustomSelect from './ui/CustomSelect';
import MobileFilterSheet from './MobileFilterSheet';
import '../styles/Header.css';
import config, { isDemoHost } from '../config/api.config';
import AuthService from '../services/authService';
import { logger } from '../utils/logger';
import { useContextFilters, PERIOD_OPTIONS } from '../hooks/useContextFilters';
import { useMobileShell } from '../hooks/useMobileShell';
import { useMobileShellContext } from '../contexts/MobileShellContext';
import { isDeskAdmin } from '../utils/authHelpers';
import { APP_VERSION_BADGE } from '../data/appVersion';

interface Brand {
  id: number;
  name: string;
  logo?: string;
}

interface Branch {
  id: number;
  name: string;
  brandId: number;
  logo?: string;
}

interface User {
  name: string;
  role: string;
  avatar?: string;
  isAdmin?: boolean;
}

interface HeaderProps {
  onBrandChange: (brand: Brand | null) => void;
  onBranchChange: (branch: Branch | null) => void;
  onPeriodChange: (period: string) => void;
  onDashboardSelect?: (dashboard: string) => void;
  disablePeriodSelector?: boolean;
  disableBranchSelector?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onBrandChange,
  onBranchChange,
  onPeriodChange,
  disablePeriodSelector = false,
  disableBranchSelector = false,
}) => {
  const navigate = useNavigate();
  const isMobileShell = useMobileShell();
  const { toggleMobileMenu } = useMobileShellContext();

  const {
    brands,
    branches,
    selectedBrand,
    selectedBranch,
    selectedPeriod,
    loading,
    branchSearchLoading,
    error,
    clearError,
    handleBrandChange,
    handleBrandSearchChange,
    handleBranchChange,
    handlePeriodChange,
    brandOptions,
    branchOptions,
  } = useContextFilters({ onBrandChange, onBranchChange, onPeriodChange });

  const [user, setUser] = useState<User>({
    name: 'در حال بارگذاری…',
    role: '',
    avatar: undefined,
  });
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setUser((prev) => ({
          ...prev,
          name: 'در حال بارگذاری…',
          role: 'در حال دریافت اطلاعات',
        }));

        const userInfo = await AuthService.fetchUserInfo(selectedBrand?.id, selectedBranch?.id);
        const admin = isDeskAdmin(userInfo);
        setUser({
          name: userInfo.userFullName || 'کاربر',
          role: admin
            ? 'مدیر سیستم'
            : userInfo.productRoles?.length > 0
              ? userInfo.productRoles[0].roleName
              : 'کاربر',
          isAdmin: admin,
          avatar: undefined,
        });
      } catch (fetchError) {
        logger.error('Failed to fetch user info', 'Header', fetchError);
        setUser({ name: 'کاربر', role: 'نامشخص', avatar: undefined });
      }
    };

    if (AuthService.isAuthenticated() && selectedBrand) {
      fetchUserInfo();
    }
  }, [selectedBrand, selectedBranch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      AuthService.clearAll();
      if (!config.STAGE_NO_AUTH) {
        if (isDemoHost()) {
          window.location.href = '/demo-login';
        } else {
          AuthService.redirectToLogin();
        }
      }
    } catch (logoutError) {
      logger.error('Logout error', 'Header', logoutError);
      AuthService.clearAll();
      if (!config.STAGE_NO_AUTH) {
        if (isDemoHost()) {
          window.location.href = '/demo-login';
        } else {
          AuthService.redirectToLogin();
        }
      }
    }
  };

  const handleEnvHandoff = () => {
    try {
      const url = AuthService.getEnvHandoffUrl(
        config.isStagingHost() ? 'prod' : 'stage',
        {
          brandId: selectedBrand?.id,
          branchId: selectedBranch?.id,
        }
      );
      window.location.href = url;
    } catch (handoffError) {
      logger.error('Failed to start environment handoff', 'Header', handoffError);
      alert('امکان انتقال بین محیط‌ها نیست: refresh token در دسترس نیست. لطفاً دوباره وارد شوید.');
    }
  };

  return (
    <>
      <header
        className={`header-inline bg-surface-elevated/95 border-b border-border-muted backdrop-blur-xl shadow-lg theme-transition sticky top-0 z-40 relative ${
          isMobileShell ? 'mobile-shell-compact-header' : ''
        }`}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-chart-color-1/3 via-transparent to-chart-color-2/3 opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-chart-color-1 via-chart-color-2 to-chart-color-3" />

        <div className="w-full relative z-10">
          <div className="header-content flex items-center justify-between gap-4 min-h-[80px]">
            <div className="flex items-center gap-3 flex-shrink-0">
              {isMobileShell && (
                <button
                  type="button"
                  className="mobile-only-shell flex items-center justify-center rounded-lg border border-border-muted bg-surface-secondary p-2 text-content-primary lg:hidden"
                  style={{ minWidth: 'var(--touch-target-min, 44px)', minHeight: 'var(--touch-target-min, 44px)' }}
                  onClick={toggleMobileMenu}
                  aria-label="باز کردن منو"
                >
                  <Menu size={20} />
                </button>
              )}

              <div className="branch-logo flex items-center flex-shrink-0">
                {selectedBranch?.logo && (
                  <div className="branch-logo-wrapper">
                    <img
                      src={selectedBranch.logo}
                      alt={selectedBranch.name}
                      className="branch-logo-img"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {!selectedBranch?.logo && selectedBrand?.logo && (
                  <div className="branch-logo-wrapper">
                    <img
                      src={selectedBrand.logo}
                      alt={selectedBrand.name}
                      className="branch-logo-img"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="header-filters desktop-only-filters flex items-center gap-3">
                <div className="filters-inline flex items-center gap-3">
                  <div className="filter-group">
                    <CustomSelect
                      options={brandOptions}
                      value={selectedBrand ? { value: selectedBrand.id, label: selectedBrand.name } : null}
                      onChange={handleBrandChange}
                      placeholder="انتخاب برند"
                      label="برند"
                      isSearchable
                      isLoading={loading || branchSearchLoading}
                      onSearchChange={handleBrandSearchChange}
                      searchPlaceholder="جستجو برند یا کد شعبه"
                    />
                  </div>
                  <div className="filter-group">
                    <CustomSelect
                      options={branchOptions}
                      value={selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null}
                      onChange={handleBranchChange}
                      placeholder="انتخاب شعبه"
                      label="شعبه"
                      isSearchable
                      isLoading={loading}
                      isDisabled={disableBranchSelector || !selectedBrand || branches.length === 0}
                    />
                  </div>
                  {!disablePeriodSelector && (
                    <div className="filter-group">
                      <CustomSelect
                        options={PERIOD_OPTIONS}
                        value={selectedPeriod}
                        onChange={handlePeriodChange}
                        placeholder="انتخاب دوره"
                        label="دوره"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="app-logo">
              <div className="flex items-center gap-2 sm:gap-3 group pointer-events-auto">
                <div className="logo-container relative">
                  <img src="/Insight.png" alt="Smart Insight Logo" className="w-auto hover:shadow-lg transition-all" />
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/changelog${window.location.search}`)}
                  className="shrink-0 rounded-md border border-border-muted bg-surface-secondary/80 px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold text-content-secondary whitespace-nowrap hover:bg-surface-secondary transition-colors"
                  aria-label={`نسخه ${APP_VERSION_BADGE} — مشاهده تاریخچه تغییرات`}
                >
                  {APP_VERSION_BADGE}
                </button>
              </div>
            </div>

            <div className="header-actions flex items-center gap-3 flex-shrink-0">
              {user.isAdmin && !config.STAGE_NO_AUTH && !isDemoHost() && (
                <button
                  type="button"
                  onClick={handleEnvHandoff}
                  className="hidden sm:inline-flex items-center rounded-lg border border-border-muted bg-surface-secondary/70 px-3 py-2 text-sm font-medium text-content-primary hover:bg-surface-secondary transition-colors"
                >
                  {config.isStagingHost() ? 'ورود به محیط اصلی' : 'ورود به staging'}
                </button>
              )}
              <div className="relative" ref={dropdownRef}>
                <div
                  className="user-profile-inline flex items-center gap-2 group cursor-pointer bg-surface-secondary/70 hover:bg-surface-secondary/80 rounded-lg px-3 py-2 transition-all border border-border-muted/60"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <div className="user-info text-right hidden sm:block">
                    <div className="text-sm font-semibold text-content-primary leading-tight">{user.name}</div>
                    <div className="text-xs text-content-secondary">{user.role}</div>
                  </div>
                  <div className="user-avatar relative">
                    <div className="w-8 h-8 bg-gradient-to-br from-chart-color-1 to-chart-color-2 rounded-lg flex items-center justify-center">
                      <div className="text-sm font-bold text-theme-inverse">{user.name.charAt(0)}</div>
                    </div>
                  </div>
                </div>

                {showUserDropdown && (
                  <div
                    className="absolute left-0 mt-2 w-48 bg-surface-elevated rounded-lg shadow-xl border border-border-muted animate-fade-in"
                    style={{
                      zIndex: 'var(--z-index-popover, 1060)',
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      marginTop: '0.5rem',
                    }}
                  >
                    <div className="p-1">
                      {user.isAdmin && !config.STAGE_NO_AUTH && !isDemoHost() && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowUserDropdown(false);
                            handleEnvHandoff();
                          }}
                          className="w-full text-right px-3 py-2 text-sm text-content-primary hover:bg-surface-secondary rounded transition-colors sm:hidden"
                        >
                          {config.isStagingHost() ? 'ورود به محیط اصلی' : 'ورود به staging'}
                        </button>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full text-right px-3 py-2 text-sm text-content-primary hover:bg-surface-secondary rounded transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-intent-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        <span className="flex-1">خروج</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-banner bg-intent-error-bg/80 border-t border-intent-error-border backdrop-blur-sm px-4 lg:px-6 py-4 animate-slide-down shadow-lg relative overflow-hidden">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div className="flex-1">
                <p className="text-sm font-semibold text-intent-error leading-tight">مشکلی در دریافت اطلاعات پیش آمد</p>
                <p className="text-xs text-intent-error/80 mt-1">{error}</p>
              </div>
              <button
                onClick={clearError}
                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-intent-error hover:bg-intent-error/10 transition-all"
                aria-label="بستن پیام خطا"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </header>

      {isMobileShell && (
        <MobileFilterSheet
          selectedBrandLabel={selectedBrand?.name ?? ''}
          selectedBranchLabel={selectedBranch?.name ?? ''}
          selectedPeriodLabel={selectedPeriod.label}
          brandOptions={brandOptions}
          branchOptions={branchOptions}
          selectedBrandValue={selectedBrand ? { value: selectedBrand.id, label: selectedBrand.name } : null}
          selectedBranchValue={selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null}
          selectedPeriodValue={selectedPeriod}
          onBrandChange={handleBrandChange}
          onBrandSearchChange={handleBrandSearchChange}
          onBranchChange={handleBranchChange}
          onPeriodChange={handlePeriodChange}
          loading={loading || branchSearchLoading}
          disablePeriodSelector={disablePeriodSelector}
          disableBranchSelector={disableBranchSelector}
          hasBrand={!!selectedBrand}
        />
      )}
    </>
  );
};

export default Header;
